// Privacy.js
// Full Rosterable LLC Privacy Policy. Public route, reached from the TopBar menu.
// Layout + typography come from the shared LegalDocument component.

import React from "react";
import LegalDocument, { H2, H3, P, Strong, Bullets, Mail } from "../components/LegalDocument";

export default function Privacy() {
  return (
    <LegalDocument title="Privacy Policy" effectiveDate="April 12, 2026">
      <P>
        This Privacy Policy explains how Rosterable LLC (“we,” “us,” or “our”) collects,
        uses, and protects information when you use the Rosterable mobile application
        (the “App”). By downloading or using the App, you agree to the practices described
        in this policy.
      </P>

      <H2>1. Information We Collect</H2>

      <H3>1.1 Information You Provide</H3>
      <P>When you create an account, we collect:</P>
      <Bullets items={[
        "Username (chosen by you; displayed publicly on leaderboards)",
        "Email address (used for account authentication, password reset, and support)",
        "Password (stored in encrypted/hashed form via Firebase Authentication; we never store it in plain text)",
      ]} />
      <P>When you use the App, you may also provide:</P>
      <Bullets items={[
        "Dynasty favorite team nicknames (optional; stored on our servers linked to your account)",
        "In-app preferences including your favorite sport, favorite team per sport, haptic feedback settings, and notification preferences",
        "Feedback messages submitted through the in-app feedback form",
      ]} />

      <H3>1.2 Information Collected Automatically</H3>
      <P>When you use the App, we and our third-party service providers automatically collect:</P>
      <Bullets items={[
        "Gameplay data including game results, statistics, roster data, progression history, and in-app achievements (synced to your account via Google Firebase)",
        "Leaderboard submissions including username and scores, which may be visible to other users",
        "Subscription tier and purchase status (managed by Apple; we receive only confirmation of an active subscription or completed purchase)",
        "Ad interaction data (managed by Google AdMob; see Section 4)",
        "Crash reports, device state, and stack traces at the time of a crash (via Firebase Crashlytics)",
        "Device information such as device model and operating system version",
      ]} />

      <H3>1.3 Guest Users</H3>
      <P>
        If you use the App as a guest without creating an account, no personal information
        is collected or transmitted to our servers. All guest data is tracked locally on your
        device only and is not backed up or associated with any account.
      </P>

      <H2>2. How We Use Your Information</H2>
      <P>We use the information we collect to:</P>
      <Bullets items={[
        "Create and manage your account",
        "Authenticate your identity when you log in and when you reset your password",
        "Display your username and scores on public leaderboards",
        "Sync your draft history, streak data, Dynasty roster, badges, and preferences across devices",
        "Process and manage your subscription and in-app purchases through Apple In-App Purchase",
        "Deliver and optimize advertisements through Google AdMob",
        "Identify and fix crashes and stability issues via Firebase Crashlytics",
        "Respond to your support requests and feedback submissions",
        "Promote the App and engage with the user community through marketing and social media",
        "Enforce our Terms of Service and protect the security of the App",
      ]} />
      <P>
        We may use aggregated or anonymized game data — such as high scores, leaderboard
        standings, and team compositions — for marketing and promotional purposes. We will
        not identify you by name in marketing materials without your consent.
      </P>

      <H2>3. How We Share Your Information</H2>

      <H3>3.1 Public Information</H3>
      <P>
        Your username and leaderboard scores are publicly visible to all users of the App.
        Do not use your real name or any personally identifiable information as your username
        if you do not wish it to be publicly displayed.
      </P>

      <H3>3.2 Third-Party Service Providers</H3>
      <P>We share information with the following third-party providers who assist in operating the App:</P>
      <P>
        <Strong>Google Firebase</Strong> — provides backend infrastructure including
        authentication, database, and crash reporting. Firebase Crashlytics automatically
        collects crash reports including device state, OS version, and stack traces to help us
        identify and fix stability issues. Firebase is subject to Google’s Privacy Policy.
      </P>
      <P>
        <Strong>Google AdMob</Strong> — serves optional rewarded video advertisements to free
        tier users who choose to watch them in exchange for bonus gameplay. AdMob may use device
        identifiers and usage data to deliver personalized ads. PRO subscribers never receive ads.
        You can opt out of personalized advertising at any time in Settings &gt; Privacy &amp;
        Security &gt; Tracking. AdMob is subject to Google’s Privacy Policy.
      </P>
      <P>
        <Strong>Apple Inc.</Strong> — processes all in-app subscription and purchase transactions.
        Apple does not share your payment information with us. We receive only confirmation of an
        active or expired subscription or completed purchase. Apple’s privacy practices are governed
        by Apple’s Privacy Policy.
      </P>

      <H3>3.3 Legal Requirements</H3>
      <P>
        We may disclose your information if required to do so by law, regulation, or valid legal
        process, or if we believe in good faith that disclosure is necessary to protect the rights,
        property, or safety of Rosterable LLC, our users, or the public.
      </P>

      <H3>3.4 Business Transfers</H3>
      <P>
        If Rosterable LLC is involved in a merger, acquisition, or sale of assets, your information
        may be transferred as part of that transaction. We will notify you via email or prominent
        in-app notice before your information is transferred and becomes subject to a different
        privacy policy.
      </P>

      <H2>4. Advertising</H2>
      <P>
        Free tier users may choose to watch optional rewarded video advertisements, served by
        Google AdMob, to unlock bonus gameplay — such as a bonus draft or an additional streak.
        Watching rewarded ads is entirely voluntary and never required to use the App. PRO
        subscribers receive a fully ad-free experience and will never see rewarded ad prompts.
        Additional ad formats may be introduced in future versions of the App.
      </P>
      <P>
        On your first launch, the App will request your permission to track your activity across
        apps and websites using your device’s advertising identifier (IDFA), as required by Apple’s
        App Tracking Transparency framework. If you grant permission, AdMob may use your IDFA to
        serve personalized advertisements. If you deny permission, you will still see
        non-personalized advertisements.
      </P>
      <P>You can change your tracking preference at any time:</P>
      <Bullets items={[
        "Go to Settings > Privacy & Security > Tracking and toggle Rosterable on or off.",
        "You can also reset your advertising identifier in Settings > Privacy & Security > Apple Advertising.",
      ]} />

      <H2>5. Data Storage and Security</H2>
      <P>
        Your account data, game history, leaderboard scores, badges, preferences, and Dynasty
        rosters are stored on secure cloud servers operated by Google. A small amount of
        non-personal data may be stored locally on your device to support app functionality.
      </P>
      <P>
        We implement industry-standard security measures including encrypted data transmission
        (TLS), hashed password storage, and access controls to protect your data. However, no
        method of electronic transmission or storage is 100% secure, and we cannot guarantee
        absolute security.
      </P>

      <H2>6. Data Retention</H2>
      <P>
        We retain your account data for as long as your account is active. If you delete your
        account through the Settings screen in the App, we will promptly delete your personal
        information, game history, leaderboard entries, and all associated data, except where
        retention is required by law. Your chosen username will be permanently retired and cannot
        be re-registered by any other user.
      </P>

      <H2>7. Your Rights and Choices</H2>

      <H3>7.1 Account Management</H3>
      <P>
        You may reset your password at any time using the “Forgot Password?” link on the login
        screen — we will send a reset link to your registered email address. You may permanently
        delete your account at any time using the “Delete Account” option in the App’s Settings
        screen. Deletion is irreversible and will remove all your associated data.
      </P>

      <H3>7.2 California Privacy Rights (CCPA)</H3>
      <P>
        If you are a California resident, you have the following rights under the California
        Consumer Privacy Act:
      </P>
      <Bullets items={[
        "The right to know what personal information we collect, use, share, or sell",
        "The right to request deletion of your personal information",
        "The right to opt out of the sale of your personal information (we do not sell personal information)",
        "The right to non-discrimination for exercising your privacy rights",
      ]} />
      <P>To exercise these rights, contact us at <Mail />.</P>

      <H3>7.3 EU and UK Users (GDPR)</H3>
      <P>
        If you are located in the European Union or United Kingdom, you have additional rights
        under the General Data Protection Regulation (GDPR) or UK GDPR:
      </P>
      <Bullets items={[
        "Right of access — you may request a copy of the personal data we hold about you.",
        "Right to rectification — you may request correction of inaccurate or incomplete data.",
        "Right to erasure — you may request deletion of your personal data (“right to be forgotten”).",
        "Right to restriction — you may request that we limit how we use your data.",
        "Right to data portability — you may request your data in a structured, machine-readable format.",
        "Right to object — you may object to processing based on our legitimate interests.",
      ]} />
      <P>
        Our legal basis for processing your personal data is performance of a contract (to provide
        the App’s features and services) and, where applicable, our legitimate interests in
        improving the App and preventing fraud.
      </P>
      <P>
        To exercise any of these rights, contact us at <Mail />. You also have the right to lodge a
        complaint with your local data protection supervisory authority.
      </P>

      <H2>8. Children’s Privacy</H2>
      <P>
        The App is not directed to children under the age of 13, and we do not knowingly collect
        personal information from children under 13. Account creation requires users to confirm they
        are at least 13 years old. If you are a parent or guardian and believe your child has
        provided us with personal information, please contact us immediately at <Mail /> and we will
        delete that information promptly.
      </P>

      <H2>9. Third-Party Links and Services</H2>
      <P>
        The App may contain links to third-party websites or services. This Privacy Policy does not
        apply to those third parties, and we are not responsible for their privacy practices. We
        encourage you to review the privacy policies of any third-party services you use.
      </P>

      <H2>10. Changes to This Privacy Policy</H2>
      <P>
        We may update this Privacy Policy from time to time. When we make material changes, we will
        notify you by updating the Effective Date at the top of this policy and, where appropriate,
        by sending an in-app notification or email. Your continued use of the App after the changes
        take effect constitutes your acceptance of the updated policy.
      </P>

      <H2>11. Contact Us</H2>
      <P>
        If you have questions, concerns, or requests regarding this Privacy Policy or your personal
        information, please contact us:
      </P>
      <P>
        Rosterable LLC<br />
        <Mail />
      </P>
    </LegalDocument>
  );
}
